/**
 * FloCanvas — 공급 흐름도의 뷰포트(툴바 + 이동·확대 캔버스 + 고정 거터·눈금자).
 *
 * 이동·확대 상태(view)는 이 컴포넌트 안에만 둔다.
 * 그래야 끌기·휠 매 프레임에 FloView 와 무거운 SVG(children)가 다시 그려지지 않고, 고정 오버레이만 다시 그려진다.
 * 거터·눈금자가 가리는 폭·높이(FLO_GUTTER_W·FLO_RULER_H)는 맞춤과 확대 버튼의 중심 계산에서 뺀다.
 * 줌 배율은 휠이면 포인터를, 버튼이면 보이는 내용 영역의 가운데를 기준으로 잡는다.
 * 끌기·휠 갱신은 프레임당 한 번만 반영한다 — 고주사율 포인터는 한 프레임에 이벤트를 여러 번 보낸다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";

import FloViewOverlay from "@plannel/components/flo/FloViewOverlay";
import { FLO_COLORS, FLO_GUTTER_W, FLO_RULER_H } from "@plannel/components/flo/floTokens";

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
// 내용이 작아도 이 배율 이상은 키우지 않는다 — 선·글자가 뭉개진다.
const FIT_MAX_SCALE = 1.25;
const FIT_PADDING = 24;
const ZOOM_STEP = 1.2;
const WHEEL_STEP = 1.12;
// 이 거리(px)를 넘겨야 끌기로 본다 — 그 아래는 손떨림이라 클릭(선택)으로 넘긴다.
const DRAG_THRESHOLD_PX = 4;

export const FLO_TOOL_BTN_SX = { width: 26, height: 26, border: 1, borderColor: "divider", borderRadius: 1 };

const clampScale = (v) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

/** 두 배율 사이 비율로, 기준점(px, py)이 화면에서 제자리에 머물게 옮긴다. */
function zoomAround(prev, factor, px, py) {
  const scale = clampScale(prev.scale * factor);
  const ratio = scale / prev.scale;
  return { scale, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio };
}

export default function FloCanvas({
  geometry, fonts, legend, controls, fitKey, onBackgroundClick, children,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  // 사용자가 옮기거나 확대한 뒤에는 크기 변화로 자동 맞춤하지 않는다 — 끌어 둔 위치가 튕겨 나간다.
  const userAdjustedRef = useRef(false);
  // 끌기로 끝난 제스처가 뒤따르는 click 으로 선택을 바꾸지 않게 한 번 삼킨다.
  const suppressClickRef = useRef(false);
  const { width: contentWidth, height: contentHeight } = geometry;

  // 다음 프레임에 반영할 갱신(이전 갱신들을 이어 붙인 함수)과 예약된 프레임 번호.
  const pendingRef = useRef(null);
  const frameRef = useRef(0);
  const scheduleView = useCallback((update) => {
    const before = pendingRef.current;
    pendingRef.current = before ? (v) => update(before(v)) : update;
    if (frameRef.current) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      const apply = pendingRef.current;
      pendingRef.current = null;
      if (apply) setView(apply);
    });
  }, []);
  useEffect(() => () => window.cancelAnimationFrame(frameRef.current), []);

  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el || !contentWidth || !contentHeight) return;
    const cw = el.clientWidth - FLO_GUTTER_W - FIT_PADDING * 2;
    const ch = el.clientHeight - FLO_RULER_H - FIT_PADDING * 2;
    if (cw <= 0 || ch <= 0) return;
    const scale = clampScale(Math.min(FIT_MAX_SCALE, cw / contentWidth, ch / contentHeight));
    // 맞춤이 이긴다 — 아직 반영 안 된 끌기·휠 갱신이 뒤이어 덮어쓰지 않게 버린다.
    pendingRef.current = null;
    setView({
      scale,
      x: FLO_GUTTER_W + (el.clientWidth - FLO_GUTTER_W - contentWidth * scale) / 2,
      y: FLO_RULER_H + (el.clientHeight - FLO_RULER_H - contentHeight * scale) / 2,
    });
  }, [contentWidth, contentHeight]);

  // 내용이 바뀌면(fitKey) 한 번 맞추고 자동 맞춤을 다시 켠다.
  useLayoutEffect(() => {
    userAdjustedRef.current = false;
    fit();
  }, [fit, fitKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (userAdjustedRef.current) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [fit]);

  // 휠 확대는 페이지 스크롤을 막아야 해서 passive:false 로 직접 붙인다(React onWheel 은 passive).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      userAdjustedRef.current = true;
      const rect = el.getBoundingClientRect();
      const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      scheduleView((prev) => zoomAround(prev, factor, px, py));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scheduleView]);

  const zoomBy = (factor) => {
    userAdjustedRef.current = true;
    const el = containerRef.current;
    if (!el) return;
    const px = FLO_GUTTER_W + (el.clientWidth - FLO_GUTTER_W) / 2;
    const py = FLO_RULER_H + (el.clientHeight - FLO_RULER_H) / 2;
    scheduleView((prev) => zoomAround(prev, factor, px, py));
  };

  const endDrag = (e) => {
    const drag = dragRef.current;
    suppressClickRef.current = Boolean(drag?.moved);
    dragRef.current = null;
    setDragging(false);
    const id = e?.pointerId ?? drag?.pointerId;
    if (id != null && e?.currentTarget?.hasPointerCapture?.(id)) e.currentTarget.releasePointerCapture(id);
  };

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    // 새 제스처가 시작되면 지난 제스처가 남긴 클릭 삼키기를 지운다.
    suppressClickRef.current = false;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY, originX: view.x, originY: view.y, moved: false, pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    // 캔버스 밖에서 버튼을 뗐으면 pointerup 이 오지 않는다 — 버튼 상태로 제스처를 접는다.
    if (e.buttons === 0) {
      endDrag(e);
      return;
    }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      userAdjustedRef.current = true;
      setDragging(true);
      // 캡처는 실제로 끌기 시작할 때만 건다 — 미리 걸면 뒤따르는 click 의 대상이 노드가 아니라 컨테이너가 된다.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    scheduleView((prev) => ({ ...prev, x: drag.originX + dx, y: drag.originY + dy }));
  };

  const onClickCapture = (e) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, minWidth: 0 }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        px: 1.5, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0,
      }}>
        {legend}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {controls}
          <Tooltip title={t("zoomIn")}>
            <IconButton size="small" sx={FLO_TOOL_BTN_SX} onClick={() => zoomBy(ZOOM_STEP)} aria-label={t("zoomIn")}>
              <ZoomInIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("zoomOut")}>
            <IconButton size="small" sx={FLO_TOOL_BTN_SX} onClick={() => zoomBy(1 / ZOOM_STEP)}
                        aria-label={t("zoomOut")}>
              <ZoomOutIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("fitToScreen")}>
            <IconButton size="small" sx={FLO_TOOL_BTN_SX} onClick={fit} aria-label={t("fitToScreen")}>
              <FitScreenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* 노드·엣지 클릭은 각자 전파를 멈추므로, 여기까지 올라온 클릭은 배경 클릭이다. */}
      <Box
        ref={containerRef}
        data-flo-canvas
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
        onClick={() => onBackgroundClick?.()}
        sx={{
          flex: 1, minHeight: 0, position: "relative", overflow: "hidden",
          bgcolor: FLO_COLORS.canvasBg, cursor: dragging ? "grabbing" : "grab", touchAction: "none",
        }}
      >
        <Box sx={{
          position: "absolute", left: 0, top: 0, width: contentWidth, height: contentHeight,
          transformOrigin: "0 0", transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
        }}>
          {children}
        </Box>
        <FloViewOverlay view={view} geometry={geometry} fonts={fonts} />
      </Box>
    </Box>
  );
}
